import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Show, UserButton, useAuth as useClerkAuth } from "@clerk/react";

import { IconGrid, IconUser } from "../components/NavIcons.js";
import { encodeQr, qrToSvg } from "../lib/qr.js";

/**
 * The landing page.
 *
 * A marketing surface, so it deliberately does not reuse the app's classes: this
 * layout is built around a photograph collage, a warm gradient wash and a
 * display face, none of which the host tool behind the sign-in wants. The styles
 * live in landing.css, scoped under `.lp`.
 *
 * Every click goes somewhere real. The CTAs lead into the create flow (which
 * bounces to sign-in when signed out), the nav links scroll to sections on this
 * page, and Pricing goes to the public /pricing rate card.
 * The reference design's Legal column and social icons are absent rather than
 * faked: neither those pages nor those accounts exist yet, and a footer of dead
 * links is worse than a shorter footer.
 */

/**
 * The QR codes on this page are real codes pointing at this deployment, so
 * scanning the screen with a phone does something. The table-tent card opens
 * the public sample album; the laptop mock's code is decorative but still
 * resolves to the site. Encoded once at module scope: the encoder tries every
 * mask and keeps the least-penalised, which is not work worth repeating on
 * each render.
 */
const QR_SVG = qrToSvg(encodeQr("https://eventgram.space"), 3);
const DEMO_QR_SVG = qrToSvg(encodeQr("https://eventgram.space/demo"), 4);

/* ------------------------------------------------------------------ */
/* Icons                                                               */
/*                                                                     */
/* Inline for the same reason the favicon is: a marketing page should  */
/* not open a request per glyph, and these are a dozen paths.          */
/* ------------------------------------------------------------------ */

function IconArrow() {
  return (
    <svg
      width="17"
      height="17"
      viewBox="0 0 16 16"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2.5 8h10.6M9.2 4.2 13 8l-3.8 3.8" />
    </svg>
  );
}

function IconNoApp() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    >
      <rect x="5.4" y="2.6" width="9.2" height="14.8" rx="2.4" />
      <path d="M3.6 3.6 16.6 16.6" />
    </svg>
  );
}

function IconNoAccount() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    >
      <circle cx="10" cy="7.1" r="3.1" />
      <path d="M4.3 16.6a6 6 0 0 1 11.4 0" />
    </svg>
  );
}

function IconAnyPhone() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    >
      <rect x="5.4" y="2.6" width="9.2" height="14.8" rx="2.4" />
      <path d="M8.7 15.3h2.6" />
    </svg>
  );
}

function IconOneTime() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 20 20"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
      <path d="M2.5 8.5h15M6 12.5h3" />
    </svg>
  );
}

function IconLock() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 20 20"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3.5" y="8" width="13" height="10" rx="2" />
      <path d="M6.5 8V5.5a3.5 3.5 0 0 1 7 0V8" />
    </svg>
  );
}

function IconLiveWall() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 20 20"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2.4" y="3.4" width="15.2" height="10.2" rx="1.8" />
      <path d="M6.4 17h7.2M10 13.6V17" />
      <path d="m8.3 7.1 3.9 1.9-3.9 1.9z" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconShield() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 20 20"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 2.5 16.1 4.7v4.8c0 3.7-2.5 6.6-6.1 8.3-3.6-1.7-6.1-4.6-6.1-8.3V4.7z" />
      <path d="m7.2 9.8 1.9 1.9 3.7-3.9" />
    </svg>
  );
}

function IconVideo() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 20 20"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="2.5" y="4.7" width="15" height="10.6" rx="2" />
      <path d="m8.6 8 4.3 2-4.3 2z" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconDownload() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 20 20"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M10 2.8v9.5M6.4 9.1 10 12.7l3.6-3.6" />
      <path d="M3.5 16.6h13" />
    </svg>
  );
}

function IconQrCode() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <path d="M14 14h3v3h-3zM17 17h4v4h-4zM14 20h3v1h-3z" />
    </svg>
  );
}

function IconUsers() {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function CheckRing() {
  return (
    <span className="lp-check-ring" aria-hidden="true">
      <svg
        width="9"
        height="9"
        viewBox="0 0 12 12"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M2.4 6.5 4.8 9 9.6 3" />
      </svg>
    </span>
  );
}

/** A drawn arrow for the hand-written notes, with the head at the photo end. */
function DoodleArrow({ up = false }: { up?: boolean }) {
  return (
    <svg
      width="52"
      height="30"
      viewBox="0 0 57 33"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      /* Mirrored, not redrawn: the note below the collage needs the same curve
         pointing back up at it. */
      style={up ? { transform: "scaleY(-1)" } : undefined}
    >
      <path d="M3 3.5c9.5 11 21 17.5 35 18" />
      <path d="M30.5 15.5 39 21.8l-8.6 5" />
    </svg>
  );
}

/* ------------------------------------------------------------------ */
/* Mock-ups                                                            */
/*                                                                     */
/* The four step cards each end in a picture of the product, drawn from */
/* the same photographs and the same pink pill the app uses, so the     */
/* walkthrough shows the thing rather than describing it. They are all  */
/* aria-hidden: a screen reader should hear the step, not a fake screen.*/
/* ------------------------------------------------------------------ */

const TILES = [
  "/landing/hero-selfie.jpg",
  "/landing/occ-birthday.jpg",
  "/landing/hero-concert.jpg",
  "/landing/occ-party.jpg",
  "/landing/story-friends.jpg",
  "/landing/hero-cheers.jpg",
  "/landing/occ-festival.jpg",
  "/landing/story-laughs.jpg",
  "/landing/occ-wedding.jpg",
];

function PhoneMock({
  variant = "gallery",
  head,
  sub,
  tiles,
  action,
}: {
  variant?: "upload" | "gallery";
  head: string;
  sub?: string;
  tiles: string[];
  action?: string;
}) {
  return (
    <div className={`lp-phone lp-phone--${variant}`} aria-hidden="true">
      <span className="lp-phone-key lp-phone-key--mute" />
      <span className="lp-phone-key lp-phone-key--vol" />
      <span className="lp-phone-key lp-phone-key--power" />

      <div className="lp-phone-screen">
        <span className="lp-phone-island" />

        <div className="lp-phone-status">
          <span>9:41</span>
          <span className="lp-phone-status-right">
            <span className="lp-phone-bars">
              <i />
              <i />
              <i />
            </span>
            <svg className="lp-phone-wifi" viewBox="0 0 12 9">
              <path d="M1.1 3.1a7.7 7.7 0 0 1 9.8 0M3.3 5.3a4.7 4.7 0 0 1 5.4 0" />
              <circle cx="6" cy="7.6" r="1.05" />
            </svg>
            <span className="lp-phone-battery">
              <i />
            </span>
          </span>
        </div>

        <div className="lp-phone-appbar">
          {variant === "gallery" ? (
            <span className="lp-phone-back">‹</span>
          ) : (
            <span className="lp-phone-logo" />
          )}
          <span className="lp-phone-appname">
            <strong>{variant === "gallery" ? head : "Eventgram"}</strong>
            <span>{variant === "gallery" ? sub : head}</span>
          </span>
          {variant === "gallery" ? (
            <span className="lp-phone-live">
              <i />
              Live
            </span>
          ) : null}
        </div>

        {variant === "gallery" ? (
          <div className="lp-phone-filters">
            <span className="lp-phone-chip lp-phone-chip--active">All</span>
            <span className="lp-phone-chip">Ceremony</span>
            <span className="lp-phone-chip">Party</span>
          </div>
        ) : null}

        <div className={`lp-mini-grid${variant === "upload" ? " lp-mini-grid--picked" : ""}`}>
          {tiles.map((src, index) => (
            <span className="lp-mini-tile" key={src}>
              <img src={src} alt="" loading="lazy" />
              {variant === "upload" && index < 3 ? <span className="lp-mini-check">✓</span> : null}
              {variant === "gallery" && index === 0 ? (
                <span className="lp-mini-heart">❤️ 24</span>
              ) : null}
            </span>
          ))}
        </div>

        {action ? (
          <div className="lp-phone-foot">
            <span className="lp-phone-pill">{action}</span>
          </div>
        ) : variant === "gallery" ? (
          <div className="lp-phone-tabbar">
            <svg className="is-on" viewBox="0 0 12 12">
              <rect x="1.2" y="1.2" width="4" height="4" rx="1" />
              <rect x="6.8" y="1.2" width="4" height="4" rx="1" />
              <rect x="1.2" y="6.8" width="4" height="4" rx="1" />
              <rect x="6.8" y="6.8" width="4" height="4" rx="1" />
            </svg>
            <svg viewBox="0 0 12 12">
              <path d="M6 10.2 1.8 6a2.8 2.8 0 0 1 4-4L6 2.2 6.2 2a2.8 2.8 0 0 1 4 4Z" />
            </svg>
            <svg viewBox="0 0 12 12">
              <path d="M6 2.2v7.6M2.2 6h7.6" />
            </svg>
          </div>
        ) : null}

        <span className="lp-phone-home" />
      </div>
    </div>
  );
}

function TentCard() {
  return (
    <div className="lp-tent-wrap" aria-hidden="true">
      <div className="lp-tent">
        <p className="lp-tent-brand">
          <span className="lp-brand-mark" />
          Eventgram
        </p>
        <p className="lp-tent-title">Summer Wedding</p>
        <span className="lp-tent-qr" dangerouslySetInnerHTML={{ __html: DEMO_QR_SVG }} />
        <p className="lp-tent-script">
          Scan &nbsp;Share
          <br />
          Be Part <span className="lp-heart">❤</span>
          <br />
          Of Our Story
        </p>
      </div>
      <div className="lp-tent-stand" />
    </div>
  );
}

function LaptopMock() {
  return (
    <div className="lp-laptop" aria-hidden="true">
      <div className="lp-laptop-lid">
        <span className="lp-laptop-camera" />
        <div className="lp-laptop-screen">
          <div className="lp-browser">
            <span className="lp-browser-dots">
              <i />
              <i />
              <i />
            </span>
            <span className="lp-browser-url">eventgram.space/new</span>
          </div>
          <div className="lp-laptop-inner">
            <div className="lp-mock-appbar">
              <p className="lp-mock-brand">
                <span className="lp-brand-mark" />
                Eventgram
              </p>
              <span className="lp-mock-tag">July 2025</span>
            </div>
            <p className="lp-mock-title">Summer Wedding</p>
            <p className="lp-mock-date">July 24, 2025</p>
            <span className="lp-mock-qr" dangerouslySetInnerHTML={{ __html: QR_SVG }} />
            <span className="lp-mock-btn">Create Event</span>
          </div>
        </div>
      </div>
      <div className="lp-laptop-base">
        <span />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Content                                                             */
/* ------------------------------------------------------------------ */

const HERO_PHOTOS = [
  {
    className: "lp-p1",
    src: "/landing/hero-selfie.jpg",
    alt: "Two guests celebrating with balloons and champagne",
  },
  {
    className: "lp-p2",
    src: "/landing/hero-concert.jpg",
    alt: "Crowd dancing under red lights at an evening event",
  },
  {
    className: "lp-p3",
    src: "/landing/hero-cheers.jpg",
    alt: "Glasses raised in a toast",
  },
  {
    className: "lp-p4",
    src: "/landing/hero-dance.jpg",
    alt: "Friends laughing together during a celebration",
  },
];

const STEPS = [
  {
    title: "Create Event",
    copy: "Pick a name, a date and a look. Your QR code is ready instantly.",
  },
  {
    title: "Share QR Code",
    copy: "Print it, display it at your event, or send it digitally to your guests.",
  },
  {
    title: "Guests Upload",
    copy: "Guests scan the code and upload straight from their phone. No app, no login.",
  },
  {
    title: "Live Gallery",
    copy: "Every photo lands in a private album that updates live during the event — and stays after it.",
  },
];

/** The picture at the foot of each step card, in the same order as STEPS. */
const STEP_MOCKS = [
  <LaptopMock key="laptop" />,
  <TentCard key="tent" />,
  <PhoneMock
    key="upload"
    variant="upload"
    head="Summer Wedding"
    sub="Private album"
    tiles={TILES.slice(0, 9)}
    action="Upload Photos"
  />,
  <PhoneMock
    key="gallery"
    head="Summer Wedding"
    sub="426 photos"
    tiles={TILES.slice(0, 9)}
  />,
];

const OCCASIONS = [
  { icon: "💍", label: "Weddings", src: "/landing/occ-wedding.jpg", alt: "Bride and groom at their wedding" },
  { icon: "🎂", label: "Birthdays", src: "/landing/occ-birthday.jpg", alt: "Birthday cake with lit candles" },
  { icon: "🪩", label: "Parties", src: "/landing/occ-party.jpg", alt: "Confetti falling over a celebration" },
  { icon: "🎤", label: "Conferences", src: "/landing/occ-conference.jpg", alt: "Audience attending a conference" },
  { icon: "🤝", label: "Meetups", src: "/landing/occ-meetup.jpg", alt: "Two people talking at a meetup" },
  { icon: "🎪", label: "Festivals", src: "/landing/occ-festival.jpg", alt: "Festival crowd with hands in the air" },
];

const STORY_PHOTOS = [
  {
    className: "lp-s1",
    src: "/landing/story-noise.jpg",
    alt: "A guest taking a photo on their phone at a bar",
  },
  {
    className: "lp-s2",
    src: "/landing/story-concert.jpg",
    alt: "Crowd lit up in blue at a concert",
  },
  {
    className: "lp-s3",
    src: "/landing/story-laughs.jpg",
    alt: "Two friends taking a selfie together",
  },
  {
    className: "lp-s4",
    src: "/landing/story-friends.jpg",
    alt: "Four friends posing together",
  },
  {
    className: "lp-s5",
    src: "/landing/story-champagne.jpg",
    alt: "Champagne glasses clinking at a table",
  },
];

const CHECKS = [
  { icon: "📸", tint: "lp-ico-a", text: "Candid photos from every guest, not just whoever held the camera" },
  { icon: "🔒", tint: "lp-ico-b", text: "A private gallery for each event" },
  { icon: "⚡", tint: "lp-ico-c", text: "A live album that fills up during the event" },
  { icon: "⬇️", tint: "lp-ico-d", text: "Guests can view and download the originals" },
  { icon: "🔗", tint: "lp-ico-e", text: "Later access with a shareable link, optional password" },
];

const ORGANIZER_FEATURES = [
  {
    icon: <IconLiveWall />,
    title: "Live TV & Projector Photo Wall",
    copy: "Plug into any venue TV, LED wall, or projector to display a real-time slideshow as guests scan and upload throughout the night.",
  },
  {
    icon: <IconShield />,
    title: "Built-In Photo Moderation",
    copy: "Review and approve guest uploads from your phone before they appear on the live wall or shared gallery, or let photos publish automatically.",
  },
  {
    icon: <IconVideo />,
    title: "Full-Resolution Photos & Videos",
    copy: "Collect original-quality smartphone photos and video clips without the heavy compression of messaging apps or social media.",
  },
  {
    icon: <IconDownload />,
    title: "One-Click ZIP Archive Download",
    copy: "Download every photo and video from your event in a single full-resolution ZIP file ready for printing, backups, or sharing.",
  },
  {
    icon: <IconLock />,
    title: "Private Link & Password Protection",
    copy: "Keep your event gallery limited to attendees with a unique shareable link and an optional guest password.",
  },
] as const;

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

export function Home() {
  const { hash } = useLocation();
  const { isSignedIn } = useClerkAuth();
  const createHref = isSignedIn ? "/events/new" : "/sign-up";

  /*
   * A plain `#section` link works while this page is already mounted, but a
   * link from /pricing to `/#section` arrives with an empty React root: the
   * browser tries to find the anchor before the SPA renders it and gives up.
   * Once the sections exist, finish the navigation ourselves. `scroll-margin-top`
   * on `.lp section` keeps the target clear of the sticky mobile nav.
   */
  useEffect(() => {
    if (!hash) return;
    let id = hash.slice(1);
    try {
      id = decodeURIComponent(id);
    } catch {
      /* malformed escape: leave the id as written */
    }
    const target = document.getElementById(id);
    if (!target) return;
    const frame = requestAnimationFrame(() => target.scrollIntoView({ block: "start" }));
    return () => cancelAnimationFrame(frame);
  }, [hash]);

  return (
    <div className="lp">
      <header className="lp-nav">
        <div className="lp-wrap lp-nav-inner">
          <Link className="lp-brand" to="/">
            <span className="lp-brand-mark" aria-hidden="true" />
            Eventgram
          </Link>

          <nav className="lp-nav-links" aria-label="Sections">
            <a href="#how-it-works">How it works</a>
            <a href="#for-organizers">For Organizers</a>
            <a href="#examples">Examples</a>
            <Link to="/pricing">Pricing</Link>
          </nav>

          <div className="lp-nav-actions">
            {/* Clerk decides which side of this to render, exactly as in the app
                header, so the landing page cannot drift out of step with the
                route guards in App.tsx. */}
            <Show when="signed-in">
              <Link className="lp-nav-signin" to="/dashboard" aria-label="Dashboard">
                <IconGrid />
                <span className="lp-nav-dashboard-text">Dashboard</span>
              </Link>
              <UserButton />
            </Show>
            <Show when="signed-out">
              <Link className="lp-nav-signin" to="/login" aria-label="Sign in">
                <IconUser />
                <span className="lp-nav-signin-text">Sign in</span>
              </Link>
            </Show>
            <Link className="lp-btn lp-btn-dark" to={createHref}>
              Create Event
            </Link>
          </div>
        </div>
      </header>

      <main>
        {/* ---------------------------------------------------------- */}
        {/* Hero                                                       */}
        {/* ---------------------------------------------------------- */}
        <section className="lp-hero">
          <div className="lp-wrap lp-hero-grid">
            <div className="lp-hero-copy">
              <p className="lp-eyebrow">REAL EVENTS. REAL PEOPLE. REAL MOMENTS.</p>
              <h1>
                One QR Code.
                <br />
                All Your <span className="lp-grad">Event Photos.</span>
              </h1>
              <p className="lp-lede">
                Collect every candid photo and video from your guests in one private album and stream
                them live on the big screen — no app download, no guest login, no subscription.
              </p>

              <div className="lp-hero-cta">
                <Link className="lp-btn lp-btn-primary lp-btn-lg" to={createHref}>
                  Create Your Free Event <IconArrow />
                </Link>
                <Link className="lp-btn lp-btn-ghost lp-btn-lg" to="/demo">
                  View Live Demo Gallery
                </Link>
              </div>
              <p className="lp-hero-note">
                <a href="#how-it-works">See how it works ↓</a>
              </p>

              <ul className="lp-trust">
                <li>
                  <IconNoApp /> No app download
                </li>
                <li>
                  <IconNoAccount /> No guest account needed
                </li>
                <li>
                  <IconOneTime /> One-time payment, no subscription
                </li>
              </ul>
            </div>

            <div className="lp-collage">
              {HERO_PHOTOS.map((photo) => (
                <img
                  key={photo.className}
                  className={`lp-photo ${photo.className}`}
                  src={photo.src}
                  alt={photo.alt}
                />
              ))}

              <TentCard />

              <div className="lp-collage-phone">
                <PhoneMock
                  variant="upload"
                  head="Summer Wedding"
                  sub="9 photos in the album"
                  tiles={TILES.slice(0, 9)}
                  action="Add your photos"
                />
              </div>

              <p className="lp-annot lp-annot-hero" aria-hidden="true">
                Candid moments from every event in one place
                <DoodleArrow />
              </p>
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------- */}
        {/* How it works                                               */}
        {/* ---------------------------------------------------------- */}
        <section className="lp-section lp-center" id="how-it-works">
          <div className="lp-wrap">
            <p className="lp-eyebrow">How Eventgram works</p>
            <h2>Simple for Everyone</h2>
            <p className="lp-sub">Create, share, and collect event photos in just a few steps.</p>

            <ol className="lp-steps-grid">
              {STEPS.map((step, index) => (
                <li className="lp-step" key={step.title}>
                  <span className="lp-step-num">{index + 1}</span>
                  <h3>{step.title}</h3>
                  <p>{step.copy}</p>
                  <div className="lp-mock">{STEP_MOCKS[index]}</div>
                </li>
              ))}
            </ol>

            <div className="lp-support-card" style={{ marginTop: "2.75rem" }}>
              <div className="lp-support-card-copy">
                <h3>Want to test the guest upload speed right now?</h3>
                <p>
                  Experience our interactive sample wedding album, preview the live TV wall, and test the scannable QR code.
                </p>
              </div>
              <Link className="lp-btn lp-btn-primary lp-btn-lg" to="/demo">
                View Live Sample Event &rarr;
              </Link>
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------- */}
        {/* For Organizers: Features                                   */}
        {/* ---------------------------------------------------------- */}
        <section className="lp-section lp-features" id="for-organizers">
          <div className="lp-wrap">
            <div className="lp-center">
              <p className="lp-eyebrow">BUILT FOR EVENT HOSTS</p>
              <h2>Everything You Need to Capture and Control the Room</h2>
              <p className="lp-sub">
                Eventgram captures the candid moments from every guest&apos;s point of view — while
                giving you full control over what appears on screen and who can access the album
                afterward.
              </p>
            </div>

            <ul className="lp-features-grid">
              {ORGANIZER_FEATURES.map((feature) => (
                <li className="lp-feature" key={feature.title}>
                  <span className="lp-feature-icon" aria-hidden="true">
                    {feature.icon}
                  </span>
                  <h3>{feature.title}</h3>
                  <p>{feature.copy}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ---------------------------------------------------------- */}
        {/* Occasions                                                  */}
        {/* ---------------------------------------------------------- */}
        <section className="lp-section lp-center" id="examples">
          <div className="lp-wrap">
            <p className="lp-eyebrow">Perfect for every occasion</p>
            <h2>Make Every Event More Memorable</h2>
            <p className="lp-sub">
              From weddings to concerts, Eventgram helps you collect real moments from the people
              who were actually there.
            </p>

            <ul className="lp-cards-grid">
              {OCCASIONS.map((occasion) => (
                <li className="lp-occ" key={occasion.label}>
                  <img src={occasion.src} alt={occasion.alt} loading="lazy" />
                  <span className="lp-chip">
                    <span aria-hidden="true">{occasion.icon}</span>
                    {occasion.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ---------------------------------------------------------- */}
        {/* Shared story                                               */}
        {/* ---------------------------------------------------------- */}
        <section className="lp-section lp-story" id="story">
          <div className="lp-wrap lp-story-grid">
            <div className="lp-collage2">
              {STORY_PHOTOS.map((photo) => (
                <img
                  key={photo.className}
                  className={`lp-photo ${photo.className}`}
                  src={photo.src}
                  alt={photo.alt}
                  loading="lazy"
                />
              ))}

              <p className="lp-annot lp-annot-a" aria-hidden="true">
                Real people
                <DoodleArrow />
              </p>
              <p className="lp-annot lp-annot-b" aria-hidden="true">
                Real moments
                <DoodleArrow up />
              </p>
              <p className="lp-annot lp-annot-c" aria-hidden="true">
                All in one place <span className="lp-heart">❤</span>
              </p>
            </div>

            <div className="lp-story-copy">
              <p className="lp-eyebrow">A shared story</p>
              <h2>More Than Just Photos</h2>
              <p className="lp-lede">
                Eventgram captures the in-between moments that made your event what it was — from
                every guest's point of view, not just whoever was holding the camera. Afterwards
                it's already all in one place.
              </p>

              <div style={{ marginTop: "1.5rem" }}>
                <Link className="lp-btn lp-btn-primary lp-btn-lg" to={createHref}>
                  Create Your Free Event <IconArrow />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------- */}
        {/* Support Banner                                             */}
        {/* ---------------------------------------------------------- */}
        <section className="lp-section lp-center" style={{ paddingTop: 0, paddingBottom: "2.5rem" }}>
          <div className="lp-wrap">
            <div className="lp-support-card">
              <div className="lp-support-card-copy">
                <h3>Have questions before your big event?</h3>
                <p>
                  Planning a wedding, corporate conference, or festival? Reach out directly to our founders
                  at <a href="mailto:support@eventgram.space" style={{ color: "var(--lp-pink)", fontWeight: 600 }}>support@eventgram.space</a>. We typically reply within hours.
                </p>
              </div>
              <a className="lp-btn lp-btn-ghost" href="mailto:support@eventgram.space">
                Email Founder Support
              </a>
            </div>
          </div>
        </section>

        {/* ---------------------------------------------------------- */}
        {/* Closing band                                               */}
        {/* ---------------------------------------------------------- */}
        <section className="lp-band">
          <div className="lp-wrap">
            <p className="lp-eyebrow">Start collecting memories today</p>
            <h2>Create Your Event in Seconds</h2>
            <p className="lp-band-sub">
              Get your QR code, start sharing, and watch the moments come in — live.
            </p>
            <Link className="lp-btn lp-btn-primary lp-btn-lg" to={createHref}>
              Create Your Event <IconArrow />
            </Link>

            <ul className="lp-band-trust">
              <li>
                <CheckRing /> Free to get started
              </li>
              <li>
                <CheckRing /> No app required
              </li>
              <li>
                <CheckRing /> Works on any device
              </li>
              <li>
                <CheckRing /> Private by default (noindex)
              </li>
            </ul>
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <div className="lp-wrap lp-footer-grid">
          <div>
            <Link className="lp-brand" to="/">
              <span className="lp-brand-mark" aria-hidden="true" />
              Eventgram
            </Link>
            <p className="lp-footer-tag">Real events. Real people. Real moments.</p>
          </div>

          <nav className="lp-footer-col" aria-label="Product">
            <h4>Product</h4>
            <a href="/#how-it-works">How It Works</a>
            <Link to="/demo">Live Demo Gallery</Link>
            <Link to="/pricing">Pricing</Link>
            <Link to="/guestcam-alternative">Eventgram vs GuestCam</Link>
            <Link to="/pov-alternative">Eventgram vs POV</Link>
            <Link to="/wedibox-alternative">Eventgram vs Wedibox</Link>
          </nav>

          <nav className="lp-footer-col" aria-label="For Organizers">
            <h4>For Organizers</h4>
            <Link to={createHref}>Create Event</Link>
            <Link to="/login">Host Sign-In</Link>
            <a href="/#for-organizers">Organizer Features</a>
            <Link to="/wedding-photo-qr-code">Wedding photo QR guide</Link>
            <Link to="/south-asian-wedding-photo-sharing">South Asian weddings</Link>
            <Link to="/holiday-party-photo-qr-code">Holiday parties</Link>
          </nav>

          <nav className="lp-footer-col" aria-label="Legal & Support">
            <h4>Legal &amp; Support</h4>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
            <a href="mailto:support@eventgram.space">Contact Support</a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
